import { useQueryClient } from "@tanstack/react-query";

import { useFetchMutation, useFetchQuery } from "common/queries/hooks";
import {
  ClientPrimaryContactPreferences,
  ClientPrimaryContactPreferencesUpdate,
  MarketingEmailUnsubscribe,
} from "common/types/inbox";
import { useAuthentication } from "common/auth/hooks";

import {
  InStockNotification,
  InStockNotificationCreate,
  MarketingEmailUnsubscribeCreateMutation,
  MarketingEmailUnsubscribeUpdateMutation,
} from "../types";
import { keys, urls } from "./constants";

const noop = () => null;

export const useInStockNotificationCreateMutation = ({
  onSuccess = noop,
}: { onSuccess?: (n: InStockNotification) => void } = {}) => {
  return useFetchMutation<InStockNotificationCreate, InStockNotification>({
    fetch: (body) => ({ method: "POST", url: urls.inStockNotifications(), body }),
    config: { onSuccess },
  });
};

export const useInStockNotificationDeleteMutation = ({
  onSuccess = noop,
}: { onSuccess?: () => void } = {}) => {
  return useFetchMutation<number, void>({
    fetch: (id) => ({ method: "DELETE", url: urls.inStockNotification(id) }),
    config: { onSuccess },
  });
};

export const useMarketingEmailUnsubscribeCreateMutation = ({
  onSuccess = noop,
}: { onSuccess?: (u: MarketingEmailUnsubscribe) => void } = {}) => {
  return useFetchMutation<MarketingEmailUnsubscribeCreateMutation, MarketingEmailUnsubscribe>({
    fetch: (body) => ({ method: "POST", url: urls.unsubscribes(), body }),
    config: { onSuccess },
  });
};

export const useMarketingEmailUnsubscribeUpdateMutation = ({
  unsubscribeId,
  onSuccess = noop,
}: {
  unsubscribeId: number;
  onSuccess?: () => void;
}) => {
  return useFetchMutation<MarketingEmailUnsubscribeUpdateMutation, MarketingEmailUnsubscribe>({
    fetch: (body) => ({ method: "PATCH", url: urls.unsubscribe(unsubscribeId), body }),
    config: { onSuccess },
  });
};

export const useClientPrimaryContactPreferencesQuery = () => {
  const { isAuthenticated } = useAuthentication();
  return useFetchQuery<ClientPrimaryContactPreferences>(keys.primaryContactPreferencesDetail(), {
    fetch: { url: urls.clientPrimaryContactPreferences() },
    config: { enabled: isAuthenticated },
  });
};

export const useUpdateClientPrimaryContactPreferencesQuery = ({
  onSuccess = noop,
}: { onSuccess?: () => void } = {}) => {
  const queryClient = useQueryClient();
  return useFetchMutation<ClientPrimaryContactPreferencesUpdate, ClientPrimaryContactPreferences>({
    fetch: (body) => ({ method: "PATCH", url: urls.clientPrimaryContactPreferences(), body }),
    config: {
      onSuccess: (newContactPreferences) => {
        queryClient.setQueryData(keys.primaryContactPreferencesDetail(), newContactPreferences);
        onSuccess();
      },
    },
  });
};
